<template>
  <div v-if="scrollElement" class="virtual-viewport-container">
    <div :style="{ height: totalSize + 'px', position: 'relative' }">
      <!-- Container optimisé avec position absolue pour éviter les reflows -->
      <div
        :style="{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          transform: `translateY(${virtualRows[0]?.start ?? 0}px)`
        }"
      >
        <div
          v-for="row in virtualRows"
          :dkey="row.key"
          :data-index="row.index"
          :ref="(el) => measureElement(el, row.index)"
          class="virtual-row"
        >
          <slot :index="row.index" />
        </div>
      </div>
    </div>
  </div>
  <div v-else ref="scrollerRef" class="virtual-viewport">
    <div :style="{ height: totalSize + 'px', position: 'relative' }">
      <!-- Container optimisé avec position absolue pour éviter les reflows -->
      <div
        :style="{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          transform: `translateY(${virtualRows[0]?.start ?? 0}px)`
        }"
      >
        <div
          v-for="row in virtualRows"
          :key="row.key"
          :data-index="row.index"
          :ref="(el) => measureElement(el, row.index)"
          class="virtual-row"
        >
          <slot :index="row.index" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount, onMounted, watch } from 'vue';
import { useVirtualizer } from '@tanstack/vue-virtual';

/**
 * Props
 */
const props = defineProps({
  // Nombre total d'items
  count: {
    type: Number,
    required: true
  },
  // Estimation de la hauteur moyenne d'un item (en px)
  // Ajuster selon vos items pour de meilleures performances
  estimateSize: {
    type: Number,
    default: 150
  },
  // Nombre d'items supplémentaires à rendre en dehors de la vue
  // Plus élevé = moins de placeholder visible au scroll rapide
  // Moins élevé = meilleure performance
  overscan: {
    type: Number,
    default: 5
  },
  // Element de scroll externe (optionnel)
  // Si fourni, utilise cet élément au lieu du scroller interne
  scrollElement: {
    type: Object,
    default: null
  }
});

/**
 * Emits pour exposer des méthodes au parent
 */
const emit = defineEmits(['ready', 'scroll']);

/**
 * Refs
 */
const scrollerRef = ref(null);

/**
 * ResizeObserver pour détecter les changements de taille des items
 * Utile pour les items avec du contenu chargé async (images, texte dynamique, etc.)
 */
const resizeObserver = new ResizeObserver((entries) => {
  for (const entry of entries) {
    const index = entry.target.getAttribute('data-index');
    if (index !== null && rowVirtualizer.value) {
      rowVirtualizer.value.measureElement(entry.target);
    }
  }
});

/**
 * Map pour suivre les éléments déjà observés
 * Évite d'ajouter le même élément plusieurs fois au ResizeObserver
 */
const observedElements = new WeakMap();

/**
 * Initialisation du virtualizer
 */
const rowVirtualizer = useVirtualizer({
  count: props.count,  // Directement props.count, pas computed
  getScrollElement: () => props.scrollElement || scrollerRef.value,

  // Estimation initiale de la taille
  // Ajustez cette valeur selon vos items pour un meilleur rendu initial
  estimateSize: () => props.estimateSize,

  // Nombre d'items à rendre en plus en dehors de la vue
  overscan: props.overscan,

  // Mesure précise de l'élément après rendu
  // C'est ce qui permet la gestion des hauteurs dynamiques
  measureElement: (el) => {
    // Utilise offsetHeight qui est plus performant que getBoundingClientRect
    return el.offsetHeight;
  }
});

// Met à jour le count quand props.count change
watch(() => props.count, (newCount) => {
  if (rowVirtualizer.value) {
    rowVirtualizer.value.options.count = newCount;
    rowVirtualizer.value.measure();
  }
});

// Réagit aux changements de scrollElement
watch(() => props.scrollElement, () => {
  if (rowVirtualizer.value) {
    rowVirtualizer.value.measure();
  }
});

/**
 * Items virtuels à rendre
 */
const virtualRows = computed(() => rowVirtualizer.value?.getVirtualItems() || []);

/**
 * Taille totale du conteneur virtuel
 */
const totalSize = computed(() => rowVirtualizer.value?.getTotalSize() || 0);

/**
 * Fonction de mesure des éléments
 * Appelée automatiquement via :ref quand un élément est monté
 */
function measureElement(el, index) {
  if (!el) return;

  // Mesure initiale pour le virtualizer
  // Note: useVirtualizer appelle déjà measureElement via son API interne,
  // mais on le fait explicitement pour s'assurer que la mesure est prise en compte
  if (rowVirtualizer.value) {
    rowVirtualizer.value.measureElement(el);
  }

  // Ajoute l'élément au ResizeObserver s'il n'est pas déjà observé
  // Cela permet de re-mesurer si le contenu change (images chargées, texte dynamique, etc.)
  if (!observedElements.has(el)) {
    resizeObserver.observe(el);
    observedElements.set(el, true);
  }
}

/**
 * Expose des méthodes utiles pour le parent
 * Via expose() le parent peut appeler ces méthodes via ref
 */
defineExpose({
  /**
   * Scroll vers un index spécifique
   * @param {number} index - L'index vers lequel scroller
   * @param {Object} options - Options de scroll
   * @param {string} options.align - 'start' | 'center' | 'end' | 'auto'
   */
  scrollToIndex: (index, options = { align: 'start' }) => {
    if (rowVirtualizer.value) {
      rowVirtualizer.value.scrollToIndex(index, options);
    }
  },

  /**
   * Scroll vers le haut de la liste
   */
  scrollToTop: () => {
    if (scrollerRef.value) {
      scrollerRef.value.scrollTop = 0;
    }
  },

  /**
   * Scroll vers le bas de la liste
   */
  scrollToEnd: () => {
    if (scrollerRef.value) {
      scrollerRef.value.scrollTop = scrollerRef.value.scrollHeight;
    }
  },

  /**
   * Force le re-calcul des tailles de tous les items visibles
   * Utile après un changement majeur de layout
   */
  recalculateSizes: () => {
    if (rowVirtualizer.value) {
      // Mesure tous les éléments actuellement dans le DOM
      const elements = scrollerRef.value?.querySelectorAll('.virtual-row');
      elements?.forEach((el) => rowVirtualizer.value.measureElement(el));
    }
  },

  /**
   * Retourne le virtualizer instance pour un accès avancé
   */
  virtualizer: computed(() => rowVirtualizer.value)
});

/**
 * Nettoyage du ResizeObserver quand le composant est démonté
 */
onBeforeUnmount(() => {
  resizeObserver.disconnect();
  if (scrollerRef.value) {
    scrollerRef.value.removeEventListener('scroll', handleScroll);
  }
});

/**
 * Gestionnaire de scroll pour émettre les événements au parent
 */
const handleScroll = (e) => {
  emit('scroll', e);
};

/**
 * Notifie le parent que le composant est prêt
 */
onMounted(() => {
  emit('ready');
  if (scrollerRef.value) {
    scrollerRef.value.addEventListener('scroll', handleScroll);
  }
});
</script>

<style scoped>
.virtual-viewport {
  height: 100%;
  overflow: auto;
  /* Optimisation CSS pour le défilement */
  -webkit-overflow-scrolling: touch;
  will-change: scroll-position;
}

.virtual-row {
  /* Optimisation du layout pour chaque row */
  contain: layout style paint;

  /* Évite les sauts de layout pendant le chargement */
  box-sizing: border-box;
}

/* Amélioration du scroll sur Firefox */
@supports (scrollbar-color: auto) {
  .virtual-viewport {
    scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
    scrollbar-width: thin;
  }
}

/* Amélioration du scroll sur WebKit (Chrome, Safari, Edge) */
.virtual-viewport::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.virtual-viewport::-webkit-scrollbar-track {
  background: transparent;
}

.virtual-viewport::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.virtual-viewport::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.3);
}
</style>
